import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { INITIAL_ACTIONS, MOCK_PROJECTS, MOCK_SCENARIOS } from "./data/mockData";

const STORAGE_KEY = "aipim:gestao-custos:demo:v1";
const GestaoCustosContext = createContext(null);

function readStoredState() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return {
      projectId: parsed?.projectId || "horizonte",
      closingId: parsed?.closingId || "2026-08",
      estimates: parsed?.estimates || {},
      actions: Array.isArray(parsed?.actions) ? parsed.actions : INITIAL_ACTIONS,
    };
  } catch (_error) {
    return { projectId: "horizonte", closingId: "2026-08", estimates: {}, actions: INITIAL_ACTIONS };
  }
}

function getBaseScenario(projectId, closingId) {
  return MOCK_SCENARIOS[`${projectId}:${closingId}`] || MOCK_SCENARIOS["horizonte:2026-08"];
}

function buildSnapshot(projectId, closingId, estimates) {
  const scenario = getBaseScenario(projectId, closingId);
  const key = `${projectId}:${closingId}`;
  const isEditable = closingId === "2026-08";
  const services = scenario.services.map((service) => {
    const edited = isEditable ? estimates[`${key}:${service.id}`] : null;
    return {
      ...service,
      additional: edited ? Number(edited.amount) : service.additional,
      estimateNote: edited?.note || "",
    };
  });
  const groupsByName = new Map();

  services.forEach((service) => {
    const group = groupsByName.get(service.group) || {
      name: service.group,
      budget: 0,
      incurred: 0,
      committed: 0,
      additional: 0,
      services: [],
    };
    group.budget += service.budget;
    group.incurred += service.incurred;
    group.committed += service.committed;
    group.additional += service.additional;
    group.services.push(service);
    groupsByName.set(service.group, group);
  });

  const groups = [...groupsByName.values()].map((group) => {
    const projected = group.incurred + group.committed + group.additional;
    return { ...group, projected, deviation: projected - group.budget };
  });
  const budget = scenario.budgetOriginal + scenario.approvedChanges;
  const incurred = services.reduce((sum, service) => sum + service.incurred, 0);
  const committed = services.reduce((sum, service) => sum + service.committed, 0);
  const additional = services.reduce((sum, service) => sum + service.additional, 0);
  const projected = incurred + committed + additional;
  const deviation = projected - budget;

  return {
    ...scenario,
    month: closingId === "2026-07" ? "Jul/26" : "Ago/26",
    services,
    groups,
    budget,
    incurred,
    committed,
    additional,
    costToComplete: committed + additional,
    projected,
    deviation,
    deviationPercent: budget ? (deviation / budget) * 100 : null,
    budgetBalance: budget - incurred,
    overrun: groups.reduce((sum, group) => sum + Math.max(group.deviation, 0), 0),
    savings: groups.reduce((sum, group) => sum + Math.abs(Math.min(group.deviation, 0)), 0),
    editable: isEditable,
  };
}

export function GestaoCustosProvider({ children }) {
  const [state, setState] = useState(readStoredState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // A demonstração continua funcional mesmo se o navegador bloquear armazenamento local.
    }
  }, [state]);

  const project = MOCK_PROJECTS.find((item) => item.id === state.projectId) || MOCK_PROJECTS[0];
  const snapshot = useMemo(
    () => buildSnapshot(state.projectId, state.closingId, state.estimates),
    [state.projectId, state.closingId, state.estimates]
  );

  const previousSnapshot = useMemo(
    () => state.closingId === "2026-08" ? buildSnapshot(state.projectId, "2026-07", {}) : null,
    [state.projectId, state.closingId]
  );

  const value = useMemo(() => ({
    project,
    projects: MOCK_PROJECTS,
    closingId: state.closingId,
    projectId: state.projectId,
    snapshot,
    previousSnapshot,
    actions: state.actions,
    setProjectId: (projectId) => setState((current) => ({ ...current, projectId })),
    setClosingId: (closingId) => setState((current) => ({ ...current, closingId })),
    saveEstimate: (serviceId, amount, note) => {
      if (state.closingId !== "2026-08" || !Number.isFinite(Number(amount)) || Number(amount) < 0 || !note.trim()) return;
      const key = `${state.projectId}:${state.closingId}:${serviceId}`;
      setState((current) => ({
        ...current,
        estimates: { ...current.estimates, [key]: { amount: Number(amount), note } },
      }));
    },
    saveAction: (action) => setState((current) => {
      const exists = current.actions.some((item) => item.id === action.id);
      return {
        ...current,
        actions: exists
          ? current.actions.map((item) => item.id === action.id ? action : item)
          : [...current.actions, { ...action, id: `act-${Date.now()}` }],
      };
    }),
    resetDemo: () => setState({ projectId: "horizonte", closingId: "2026-08", estimates: {}, actions: INITIAL_ACTIONS }),
  }), [project, snapshot, previousSnapshot, state]);

  return <GestaoCustosContext.Provider value={value}>{children}</GestaoCustosContext.Provider>;
}

export function useGestaoCustos() {
  const value = useContext(GestaoCustosContext);
  if (!value) throw new Error("useGestaoCustos deve ser usado dentro de GestaoCustosProvider.");
  return value;
}
